import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import type { ChequeTercero } from './types';
import {
  etiquetaEstadoChequeTercero,
  esChequeElectronico,
  ETIQUETA_ECHEQ,
  esChequeRechazable,
  resolverEstadoChequeTercero,
} from '../../../utils/chequeTerceroEstado';
import {
  colorVencimientoUrgencia,
  formatDestinoSalidaCheque,
  formatParteInteresadaCompact,
  getVencimientoUrgencia,
} from '../../../utils/chequeTerceroDisplay';
import { formatCuit } from '../../../utils/formatters';
import { formatNroChequeVista } from '../../../utils/nroCheque';

interface ChequesTerceroListaProps {
  cheques: ChequeTercero[];
  loading: boolean;
  isMobile: boolean;
  vistaCompacta: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  searchTerm: string;
  onPageChange: (_: unknown, newPage: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerDetalle: (cheque: ChequeTercero) => void;
  onRechazar: (cheque: ChequeTercero) => void;
  formatCurrency: (n: number) => string;
  formatDate: (s: string) => string;
}

const CeldaTruncada: React.FC<{ titulo: string; children: React.ReactNode; maxWidth?: number }> = ({
  titulo,
  children,
  maxWidth = 200,
}) => (
  <Tooltip title={titulo} placement="top-start" enterDelay={400}>
    <Typography
      noWrap
      variant="body2"
      sx={{ maxWidth, display: 'block' }}
      component="span"
    >
      {children}
    </Typography>
  </Tooltip>
);

const getEstadoChip = (cheque: ChequeTercero) => {
  const estado = resolverEstadoChequeTercero(cheque);
  const label = etiquetaEstadoChequeTercero(estado);
  const color =
    estado === 'anulado'
      ? 'default'
      : estado === 'rechazado'
        ? 'error'
        : estado === 'conciliado'
          ? 'success'
          : estado === 'entregado'
            ? 'warning'
            : 'info';
  return <Chip label={label} color={color} size="small" />;
};

const getSoporteChip = (cheque: ChequeTercero) =>
  esChequeElectronico(cheque) ? (
    <Chip label={ETIQUETA_ECHEQ} color="secondary" size="small" />
  ) : (
    <Chip label="Físico" size="small" variant="outlined" />
  );

const ChequesTerceroLista: React.FC<ChequesTerceroListaProps> = ({
  cheques,
  loading,
  isMobile,
  vistaCompacta,
  totalCount,
  page,
  rowsPerPage,
  searchTerm,
  onPageChange,
  onRowsPerPageChange,
  onVerDetalle,
  onRechazar,
  formatCurrency,
  formatDate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuCheque, setMenuCheque] = useState<ChequeTercero | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>, cheque: ChequeTercero) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuCheque(cheque);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCheque(null);
  };

  const copiarNumero = async (nro: number) => {
    try {
      await navigator.clipboard.writeText(String(nro));
      enqueueSnackbar('Número de cheque copiado', { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar', { variant: 'warning' });
    }
    closeMenu();
  };

  const pagination = (
    <TablePagination
      component="div"
      count={totalCount}
      page={page}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={[10, 20]}
      labelRowsPerPage="Filas por página:"
      labelDisplayedRows={({ from, to, count }) =>
        `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`
      }
      sx={{
        '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1, px: 1 },
      }}
    />
  );

  const emptyMsg = searchTerm
    ? 'No se encontraron cheques con los criterios de búsqueda'
    : 'No hay cheques de tercero registrados';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isMobile) {
    return (
      <>
        <Stack spacing={2}>
          {cheques.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              {emptyMsg}
            </Typography>
          ) : (
            cheques.map((cheque) => {
              const cliente = formatParteInteresadaCompact(cheque.nroCliente, cheque.nombreCliente);
              const destino = formatDestinoSalidaCheque(cheque);
              const urgencia = getVencimientoUrgencia(cheque.fechaVencimiento);
              return (
                <Card
                  key={cheque.nroCarga}
                  onClick={() => onVerDetalle(cheque)}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              #{formatNroChequeVista(cheque.nroCheque)}
                            </Typography>
                            <ChevronRightIcon fontSize="small" color="action" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {cheque.nombreBanco?.trim() || '-'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {getSoporteChip(cheque)}
                          {getEstadoChip(cheque)}
                        </Stack>
                      </Stack>
                      <Typography variant="body2">
                        <strong>Cliente:</strong> {cliente.linea}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Destino:</strong> {destino}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', textAlign: 'right' }}>
                        {formatCurrency(cheque.importe || 0)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: colorVencimientoUrgencia(urgencia) }}
                      >
                        <strong>Vence:</strong> {formatDate(cheque.fechaVencimiento)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
        {pagination}
      </>
    );
  }

  const colSpan = vistaCompacta ? 9 : 19;

  const renderVencimiento = (cheque: ChequeTercero) => {
    const urgencia = getVencimientoUrgencia(cheque.fechaVencimiento);
    return (
      <Typography variant="body2" sx={{ color: colorVencimientoUrgencia(urgencia), fontVariantNumeric: 'tabular-nums' }}>
        {formatDate(cheque.fechaVencimiento)}
      </Typography>
    );
  };

  const renderAcciones = (cheque: ChequeTercero) => (
    <IconButton
      size="small"
      aria-label="Acciones del cheque"
      onClick={(e) => openMenu(e, cheque)}
    >
      <MoreVertIcon fontSize="small" />
    </IconButton>
  );

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small" aria-label="Listado de cheques de tercero">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              {vistaCompacta ? (
                <>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Nro. Cheque</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Soporte</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Banco</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600, textAlign: 'right' }}>Importe</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Vencimiento</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Destino / Salida</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600, width: 48 }} aria-label="Acciones" />
                </>
              ) : (
                <>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Nro. Carga</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Nro. Cheque</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Soporte</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Banco</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Sucursal</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Librador</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>CUIT Librador</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Cliente</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Destino / Salida</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600, textAlign: 'right' }}>Importe</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>F. Emisión</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>F. Vencimiento</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>F. Ingreso</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>F. Salida</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Comp. Ingreso</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>Comp. Salida</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600 }}>MCP</TableCell>
                  <TableCell scope="col" sx={{ fontWeight: 600, width: 48 }} aria-label="Acciones" />
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {cheques.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{emptyMsg}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              cheques.map((cheque) => {
                const cliente = formatParteInteresadaCompact(cheque.nroCliente, cheque.nombreCliente);
                const librador = formatParteInteresadaCompact(undefined, cheque.librador?.trim() || '-', 32);
                const cuitLibrador = formatCuit(cheque.nroLibrador?.trim() || '');
                const cuitLibradorDisplay = cuitLibrador || '-';
                const destino = formatDestinoSalidaCheque(cheque);
                const destinoTitulo = destino;

                return (
                  <TableRow
                    key={cheque.nroCarga}
                    hover
                    sx={{ cursor: 'pointer', '&:hover .cheque-link': { textDecoration: 'underline' } }}
                    onClick={() => onVerDetalle(cheque)}
                  >
                    {vistaCompacta ? (
                      <>
                        <TableCell>
                          <Link
                            className="cheque-link"
                            component="button"
                            variant="body2"
                            sx={{ fontWeight: 700, textAlign: 'left' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onVerDetalle(cheque);
                            }}
                          >
                            {formatNroChequeVista(cheque.nroCheque)}
                          </Link>
                        </TableCell>
                        <TableCell>{getEstadoChip(cheque)}</TableCell>
                        <TableCell>{getSoporteChip(cheque)}</TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cheque.nombreBanco?.trim() || '-'} maxWidth={140}>
                            {cheque.nombreBanco?.trim() || '-'}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cliente.titulo} maxWidth={180}>
                            {cliente.linea}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatCurrency(cheque.importe || 0)}
                        </TableCell>
                        <TableCell>{renderVencimiento(cheque)}</TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={destinoTitulo} maxWidth={160}>
                            {destino}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell align="center">{renderAcciones(cheque)}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{cheque.nroCarga || '-'}</TableCell>
                        <TableCell>
                          <Link
                            className="cheque-link"
                            component="button"
                            variant="body2"
                            sx={{ fontWeight: 700 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onVerDetalle(cheque);
                            }}
                          >
                            {formatNroChequeVista(cheque.nroCheque)}
                          </Link>
                        </TableCell>
                        <TableCell>{getEstadoChip(cheque)}</TableCell>
                        <TableCell>{getSoporteChip(cheque)}</TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cheque.nombreBanco?.trim() || '-'} maxWidth={120}>
                            {cheque.nombreBanco?.trim() || '-'}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cheque.nombreSucursal?.trim() || '-'} maxWidth={100}>
                            {cheque.nombreSucursal?.trim() || '-'}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={librador.titulo} maxWidth={160}>
                            {librador.linea}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          <CeldaTruncada titulo={cuitLibradorDisplay} maxWidth={130}>
                            {cuitLibradorDisplay}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cliente.titulo} maxWidth={160}>
                            {cliente.linea}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={destinoTitulo} maxWidth={160}>
                            {destino}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                          {formatCurrency(cheque.importe || 0)}
                        </TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDate(cheque.fechaEmision)}</TableCell>
                        <TableCell>{renderVencimiento(cheque)}</TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDate(cheque.fechaIngreso)}</TableCell>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatDate(cheque.fechaSalida || '')}</TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cheque.comprobanteIngreso?.trim() || '-'} maxWidth={140}>
                            {cheque.comprobanteIngreso?.trim() || '-'}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>
                          <CeldaTruncada titulo={cheque.comprobanteSalida?.trim() || '-'} maxWidth={140}>
                            {cheque.comprobanteSalida?.trim() || '-'}
                          </CeldaTruncada>
                        </TableCell>
                        <TableCell>{cheque.mcp?.trim() || '-'}</TableCell>
                        <TableCell align="center">{renderAcciones(cheque)}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {pagination}
      </TableContainer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuCheque) onVerDetalle(menuCheque);
            closeMenu();
          }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          Ver detalle
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuCheque) void copiarNumero(menuCheque.nroCheque);
          }}
        >
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Copiar nº cheque
        </MenuItem>
        {menuCheque && esChequeRechazable(menuCheque) && (
          <MenuItem
            onClick={() => {
              if (menuCheque) onRechazar(menuCheque);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            <BlockIcon fontSize="small" sx={{ mr: 1 }} />
            Rechazar cheque
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ChequesTerceroLista;
